<template>
  <article
    class="crm-deal-card"
    :class="{
      'is-late': isLate,
      'is-no-action': hasNoNextAction,
      'is-won': oportunidade.status === 'ganha',
      'is-lost': oportunidade.status === 'perdida',
      'is-draggable': draggable,
      'is-dragging': dragging,
    }"
    :draggable="draggable"
    @dragend="$emit('drag-end', oportunidade)"
    @dragstart="onDragStart"
  >
    <button
      class="crm-deal-main"
      type="button"
      @click="$emit('open', oportunidade)"
    >
      <div class="crm-deal-top">
        <span v-if="flowText" class="crm-deal-flow">
          {{ flowText }}
        </span>
        <span
          class="crm-deal-temperature"
          :style="temperatureStyle"
          role="button"
          tabindex="0"
          title="Alterar temperatura"
          @click.stop.prevent="$emit('temperature', oportunidade)"
          @keydown.enter.stop.prevent="$emit('temperature', oportunidade)"
          @keydown.space.stop.prevent="$emit('temperature', oportunidade)"
        >
          <i :class="temperatureIcon"></i>
          {{ temperatureText }}
        </span>
        <span class="crm-deal-status">{{ statusText }}</span>
        <i class="la la-info-circle"></i>
      </div>
      <strong class="crm-deal-title">{{ oportunidade.titulo }}</strong>
      <span class="crm-deal-contact">{{ contactText }}</span>
      <span v-if="sourceText" class="crm-deal-source">
        <i class="la la-bullhorn"></i>
        {{ sourceText }}
      </span>
      <div class="crm-deal-meta">
        <span v-if="!hideMoney">{{ moneyText }}</span>
        <span>{{ oportunidade.usuario || "Sem responsável" }}</span>
        <span v-if="oportunidade.vendedor_responsavel">
          Vend.: {{ oportunidade.vendedor_responsavel }}
        </span>
        <span v-if="oportunidade.responsavel_tecnico">
          Dev.: {{ oportunidade.responsavel_tecnico }}
        </span>
      </div>
      <div class="crm-deal-next-action">
        <i class="la la-calendar-check-o"></i>
        <span>{{ nextActionText }}</span>
      </div>
    </button>
    <div v-if="contactPhone" class="crm-deal-whatsapp">
      <span>
        <i class="la la-whatsapp"></i>
        {{ contactPhone }}
      </span>
      <div>
        <button type="button" title="Copiar WhatsApp" @click.stop="copyPhone">
          <i class="la la-copy"></i>
        </button>
        <button type="button" title="Abrir WhatsApp" @click.stop="openWhatsapp">
          <i class="la la-external-link"></i>
        </button>
      </div>
    </div>
    <div v-if="showActions" class="crm-deal-actions">
      <button
        type="button"
        title="Criar tarefa"
        @click="$emit('task', oportunidade)"
      >
        <i class="la la-plus"></i>
        <span>Tarefa</span>
      </button>
      <button
        type="button"
        :title="scheduleButtonTitle"
        @click="$emit('schedule', oportunidade)"
      >
        <i class="la la-calendar"></i>
        <span>{{ scheduleButtonText }}</span>
      </button>
      <button
        type="button"
        title="Adicionar nota"
        @click="$emit('note', oportunidade)"
      >
        <i class="la la-sticky-note"></i>
        <span>Nota</span>
      </button>
      <button
        type="button"
        title="Mais ações"
        @click="$emit('more', oportunidade)"
      >
        <i class="la la-ellipsis-h"></i>
      </button>
    </div>
  </article>
</template>

<script>
export default {
  name: "CrmDealCard",
  props: {
    oportunidade: {
      type: Object,
      required: true,
    },
    draggable: {
      type: Boolean,
      default: true,
    },
    dragging: {
      type: Boolean,
      default: false,
    },
    showActions: {
      type: Boolean,
      default: true,
    },
    temperatureOptions: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    contactText() {
      return (
        this.oportunidade.contato ||
        this.oportunidade.pessoa ||
        "Sem cliente ou contato"
      );
    },
    flowText() {
      const labels = {
        contratos: "Contrato",
        implementacao_white_label: "Implementação",
        treinamento_cliente_final: "Treinamento Cliente Final",
        treinamento: "Treinamento",
        website: "Website",
      };

      return labels[this.oportunidade.tipo_fluxo] || "";
    },
    isTreinamentoClienteFinal() {
      return this.oportunidade?.tipo_fluxo === "treinamento_cliente_final";
    },
    scheduleButtonText() {
      return this.isTreinamentoClienteFinal ? "Agendar treinamento" : "Agenda";
    },
    scheduleButtonTitle() {
      return this.isTreinamentoClienteFinal
        ? "Agendar treinamento"
        : "Criar agendamento";
    },
    contactPhone() {
      return (
        this.oportunidade.contato_telefone ||
        this.oportunidade.telefone_contato ||
        this.oportunidade.pessoa_telefone ||
        ""
      );
    },
    sourceText() {
      const fonte = this.oportunidade.fonte;
      const campanha = this.oportunidade.campanha;

      return [fonte, campanha].filter(Boolean).join(" · ");
    },
    whatsappUrl() {
      const telefone = this.normalizarTelefoneWhatsapp(
        this.oportunidade.contato_telefone_normalizado || this.contactPhone
      );

      return telefone ? `https://web.whatsapp.com/send?phone=${telefone}` : "";
    },
    isLate() {
      return (
        this.oportunidade.proxima_atividade_em &&
        new Date(this.oportunidade.proxima_atividade_em) < new Date()
      );
    },
    hasNoNextAction() {
      return !this.oportunidade.proxima_atividade_em;
    },
    hideMoney() {
      return ["treinamento_cliente_final", "treinamento", "website"].includes(
        this.oportunidade ? this.oportunidade.tipo_fluxo : null
      );
    },
    moneyText() {
      return this.valorPrincipalOportunidade(this.oportunidade).toLocaleString(
        "pt-BR",
        {
          style: "currency",
          currency: "BRL",
        }
      );
    },
    nextActionText() {
      if (!this.oportunidade.proxima_atividade_em) {
        return "Sem próxima ação";
      }

      return new Date(this.oportunidade.proxima_atividade_em).toLocaleString(
        "pt-BR"
      );
    },
    statusText() {
      const labels = {
        aberta: "Aberta",
        ganha: "Ganha",
        perdida: "Perdida",
      };

      return (
        labels[this.oportunidade.status] || this.oportunidade.status || "-"
      );
    },
    temperatureOption() {
      return this.temperatureOptions.find(
        (option) => option.value === this.oportunidade.temperatura
      );
    },
    temperatureText() {
      if (this.temperatureOption && this.temperatureOption.text) {
        return this.temperatureOption.text;
      }

      const labels = {
        muito_frio: "Muito frio",
        frio: "Frio",
        morno: "Morno",
        quente: "Quente",
        venda_certa: "Venda certa",
      };

      return labels[this.oportunidade.temperatura] || "Sem temperatura";
    },
    temperatureIcon() {
      const icons = {
        muito_frio: "la la-thermometer-empty",
        frio: "la la-thermometer-quarter",
        morno: "la la-thermometer-half",
        quente: "la la-thermometer-three-quarters",
        venda_certa: "la la-thermometer-full",
      };

      return icons[this.oportunidade.temperatura] || "la la-thermometer-empty";
    },
    temperatureStyle() {
      const color =
        this.temperatureOption?.cor ||
        this.oportunidade.cor ||
        this.oportunidade.etapa_cor ||
        (this.isLate ? "#f64e60" : "#0bb7d4");

      return {
        borderColor: color,
        color,
      };
    },
  },
  methods: {
    valorPrincipalOportunidade(oportunidade = {}) {
      const campos = [
        oportunidade.valor_principal,
        oportunidade.valor_final,
        oportunidade.valor_negociacao,
        oportunidade.valor_proposta,
        oportunidade.valor_estimado,
      ];
      const valor = campos.find(
        (item) => item !== null && item !== undefined && item !== ""
      );

      return Number(valor || 0);
    },
    onDragStart(event) {
      if (!this.draggable) {
        event.preventDefault();
        return;
      }

      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData(
          "text/plain",
          String(this.oportunidade.id_crm_oportunidade)
        );
      }

      this.$emit("drag-start", this.oportunidade);
    },
    async copyPhone() {
      const telefone = this.contactPhone;

      if (!telefone) {
        return;
      }

      try {
        if (this.$copyText) {
          await this.$copyText(telefone);
        } else if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(telefone);
        } else {
          this.copyPhoneFallback(telefone);
        }

        if (this.$toast) {
          this.$toast.success("WhatsApp copiado.");
        }
      } catch (error) {
        this.copyPhoneFallback(telefone);

        if (this.$toast) {
          this.$toast.success("WhatsApp copiado.");
        }
      }
    },
    copyPhoneFallback(telefone) {
      const input = document.createElement("input");
      input.value = telefone;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    },
    openWhatsapp() {
      if (!this.whatsappUrl) {
        return;
      }

      window.open(this.whatsappUrl, "_blank", "noopener");
    },
    normalizarTelefoneWhatsapp(value) {
      let telefone = String(value || "").replace(/\D+/g, "");

      if (!telefone) {
        return "";
      }

      if (!telefone.startsWith("55") && telefone.length <= 11) {
        telefone = `55${telefone}`;
      }

      return telefone;
    },
  },
};
</script>

<style scoped>
.crm-deal-card {
  background: #ffffff;
  border: 1px solid #dfe5ee;
  border-radius: 8px;
  box-shadow: 0 4px 14px rgba(23, 31, 56, 0.04);
  margin-bottom: 10px;
  overflow: hidden;
  transition: border-color 0.16s ease, box-shadow 0.16s ease,
    transform 0.16s ease, opacity 0.16s ease;
}

.crm-deal-card.is-late {
  border-color: #ffd6de;
}

.crm-deal-card.is-won {
  border-color: #b9f1dd;
}

.crm-deal-card.is-lost {
  border-color: #ffd6de;
}

.crm-deal-card.is-draggable {
  cursor: grab;
}

.crm-deal-card.is-dragging {
  box-shadow: 0 10px 28px rgba(23, 31, 56, 0.16);
  opacity: 0.58;
  transform: scale(0.985);
}

.crm-deal-main {
  background: transparent;
  border: 0;
  color: #25324b;
  display: block;
  padding: 11px 12px 9px;
  text-align: left;
  width: 100%;
}

.crm-deal-main:hover {
  background: #fbfdff;
}

.crm-deal-top {
  align-items: center;
  color: #647084;
  display: flex;
  font-size: 12px;
  gap: 7px;
  margin-bottom: 8px;
}

.crm-deal-top > i {
  margin-left: auto;
}

.crm-deal-temperature {
  align-items: center;
  background: #ffffff;
  border: 1px solid currentColor;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  font-size: 11px;
  font-weight: 800;
  gap: 4px;
  line-height: 1;
  max-width: 128px;
  min-height: 22px;
  min-width: 0;
  overflow: hidden;
  padding: 4px 6px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.crm-deal-temperature:hover,
.crm-deal-temperature:focus {
  box-shadow: 0 0 0 3px rgba(11, 183, 212, 0.14);
  outline: none;
}

.crm-deal-flow {
  background: #eef7ff;
  border: 1px solid #b8dcff;
  border-radius: 4px;
  color: #2468a8;
  font-size: 11px;
  font-weight: 800;
  line-height: 1;
  min-height: 22px;
  padding: 5px 6px;
}

.crm-deal-temperature i {
  margin-left: 0;
}

.crm-deal-status {
  font-weight: 700;
}

.crm-deal-title {
  display: block;
  font-size: 14px;
  font-weight: 800;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.crm-deal-contact {
  color: #647084;
  display: block;
  font-size: 12px;
  margin-top: 4px;
  overflow-wrap: anywhere;
}

.crm-deal-source {
  align-items: center;
  color: #7a5d12;
  display: inline-flex;
  font-size: 12px;
  gap: 5px;
  margin-top: 6px;
  max-width: 100%;
  min-width: 0;
  overflow-wrap: anywhere;
}

.crm-deal-meta {
  align-items: center;
  display: flex;
  font-size: 12px;
  font-weight: 700;
  gap: 10px;
  justify-content: space-between;
  margin-top: 10px;
  min-width: 0;
}

.crm-deal-meta span {
  min-width: 0;
  overflow-wrap: anywhere;
}

.crm-deal-next-action {
  align-items: center;
  background: #f3f6fb;
  border-radius: 6px;
  color: #647084;
  display: flex;
  font-size: 12px;
  gap: 6px;
  margin-top: 10px;
  min-height: 30px;
  padding: 6px 8px;
}

.crm-deal-next-action span {
  min-width: 0;
  overflow-wrap: anywhere;
}

.is-late .crm-deal-next-action {
  background: #fff2f5;
  color: #d63952;
}

.is-no-action .crm-deal-next-action {
  background: #fff8dd;
  color: #946500;
}

.crm-deal-whatsapp {
  align-items: center;
  border-top: 1px solid #edf1f6;
  color: #1c7c54;
  display: flex;
  font-size: 12px;
  font-weight: 700;
  justify-content: space-between;
  padding: 8px 12px;
}

.crm-deal-whatsapp span {
  align-items: center;
  display: inline-flex;
  gap: 5px;
  min-width: 0;
  overflow-wrap: anywhere;
}

.crm-deal-whatsapp div {
  display: inline-flex;
  gap: 5px;
  margin-left: 8px;
}

.crm-deal-whatsapp button {
  align-items: center;
  background: #e7fff3;
  border: 1px solid #b8f1d5;
  border-radius: 4px;
  color: #0f7b4d;
  display: inline-flex;
  height: 26px;
  justify-content: center;
  padding: 0;
  width: 26px;
}

.crm-deal-whatsapp button:hover {
  background: #d6f8e8;
}

.crm-deal-actions {
  border-top: 1px solid #edf1f6;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.crm-deal-actions button {
  align-items: center;
  background: #f8fafc;
  border: 0;
  border-right: 1px solid #edf1f6;
  color: #25324b;
  display: inline-flex;
  font-size: 12px;
  font-weight: 800;
  gap: 5px;
  justify-content: center;
  min-height: 34px;
  padding: 6px;
  min-width: 0;
}

.crm-deal-actions button span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.crm-deal-actions button:last-child {
  border-right: 0;
}

.crm-deal-actions button:hover {
  background: #eaf7ff;
  color: #0b87c9;
}

@media (max-width: 575px) {
  .crm-deal-actions span {
    display: none;
  }
}
</style>
